import {
  AddCircleOutlineOutlined,
  DeleteOutlineOutlined,
  EditOutlined,
  RemoveRedEyeOutlined,
  SimCardDownloadOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  type ColDef,
  type ICellRendererParams,
  type ValueGetterParams,
} from 'ag-grid-community';

import { type AgGridReactProps } from 'ag-grid-react';
import { PNG } from 'components/Asset/PNG';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import { saveAs } from 'file-saver';
import SquaresWorkOrdersManagementHandler from 'handlers/squaresHandlers/sqauresWorkOrdersManagement.handler';
import useAppTheme from 'hooks/useAppTheme';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import {
  setActiveStep,
  updateBulk,
} from 'store/SquaresStore/WorkOrderManagement/WorkOrderCreationFormState.slice';
import { type Asset } from 'types/asset.types';
import { type ExportWorkOrderPayload } from 'types/Squares/WorkOrderManagement/work.order.management.types';
import { DATE_TIME_FORMAT } from 'utils/constants/dateTime';
import {
  convertUtcToDisplayTime,
  getCurrentUtcOffset,
} from 'utils/helpers/dateTime';
import { getImageNameByMakeModelAndAsset } from 'utils/helpers/mapAssetImage';
import ConfirmationModal from '../../../components/ConfirmationModal';
import CustomAgGrid from '../../../CustomTable/CustomAgGrid';
import {
  type ProcessedWorkOrder,
  processWorkOrdersForTabularView,
} from '../../../helpers/formatters';
import { colorMap } from '../../constants/constants';
import { ImportButton } from './ImportButton';
import WorkOrderDetailsModal from './WorkOrderDetailsModal';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface DeleteModalState {
  open: boolean;
  rowData: ProcessedWorkOrder | null;
  bulkData: string[] | null;
  dataType: 'row' | 'bulk' | null;
}

interface DetailModalState {
  open: boolean;
  data: ProcessedWorkOrder | null;
}

const statusStyle = (value: string) => {
  return {
    height: '28px',
    fontWeight: 600,
    borderRadius: '16px',
    fontSize: '12px',
    textTransform: 'capitalize',
    ...colorMap[value],
  };
};

/**
 * A reusable UI component to display key asset information in a card format,
 * styled to match the provided screenshot.
 */
const AssetDisplayCell = ({
  bumperNumber,
  assetType,
  make,
  model,
}: {
  bumperNumber: string;
  assetType: string;
  make: string;
  model: string;
}) => {
  const pngName =
    make && model && assetType
      ? getImageNameByMakeModelAndAsset(make, model, assetType, 'Small')
      : 'fallbackImageSmall';
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <PNG
        name={pngName}
        alt={''}
        style={{
          height: '50px',
          width: '70px',
        }}
      />

      <Box>
        <Typography variant="body2">{bumperNumber}</Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
          color={'text.secondary'}
        >
          <Typography
            variant="body2"
            textTransform={'capitalize'}
            fontSize={'12px'}
          >
            {assetType?.split('_')?.join(' ')}
          </Typography>
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.5)',
              height: '16px',
              alignSelf: 'center',
            }}
          />
          <Typography
            variant="body2"
            fontSize={'12px'}
          >{`${make}-${model}`}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

const TableWithLoader = ({
  rowData,
  columnDefs,
  quickFilter,
  setSelectedIds,
}: {
  rowData: ProcessedWorkOrder[];
  columnDefs: AgGridReactProps['columnDefs'];
  quickFilter: string;
  setSelectedIds: (ids: string[]) => void;
}) => {
  const isLoading = useAppSelector(
    (state) => state.sqauresWorkOrderManagementReducer.loading
  );
  return (
    <>
      {isLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          <SingleFadeLoader isFullScreen={false} />
          <div style={{ height: '20px' }} />
        </div>
      ) : (
        <CustomAgGrid
          rowData={rowData}
          columnDefs={columnDefs}
          animateRows
          pagination
          paginationPageSize={10}
          rowSelection="multiple"
          suppressRowClickSelection
          domLayout="normal"
          headerHeight={50}
          rowHeight={60}
          suppressMovableColumns={true}
          onSelectionChanged={(e: any) => {
            setSelectedIds(
              e.api.getSelectedRows().map((r: ProcessedWorkOrder) => r.id)
            );
          }}
          quickFilterText={quickFilter}
        />
      )}
    </>
  );
};

const Table = ({
  handleCreateOrEditWorkOrderClick,
}: {
  handleCreateOrEditWorkOrderClick: (
    view: 'tabular' | 'creation',
    action: 'create' | 'update'
  ) => void;
}) => {
  const { t } = useTranslation();
  const workOrdersManagementHandler = new SquaresWorkOrdersManagementHandler();
  const dispatch = useAppDispatch();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const internalUserList = useAppSelector(
    (state) => state.internalUserReducer?.internalUserList
  );
  const externalUserList = useAppSelector(
    (state) => state.externalUserReducer?.externalUserList
  );

  const assets: Asset[] | undefined = useAppSelector(
    (state) => state.assetReducer?.assets
  );

  const workOrderData = useAppSelector(
    (state) => state.sqauresWorkOrderManagementReducer.all
  );
  const { preferredTimeDisplayMode, timezone, timezoneBasedOnDisplayMode } =
    useTimeDisplayMode();

  const rowData = useMemo(() => {
    const allUsers = [...externalUserList, ...internalUserList];
    const assetMap = Object.fromEntries(
      assets.map((a) => [
        a.assetId,
        {
          bumperNumber: a.bumperNumber,
          make: a.make,
          model: a.model,
          assetType: a.assetType,
        },
      ])
    );
    const userMap = Object.fromEntries(
      allUsers.map((u) => [u.userId, `${u.firstName} ${u.lastName}`])
    );
    const filteredData = workOrderData?.filter((el) => el?.isApproved);
    const temp = processWorkOrdersForTabularView(
      filteredData,
      assetMap,
      userMap
    );
    return temp;
  }, [workOrderData, externalUserList, internalUserList, assets]);

  // state
  const [quickFilter, setQuickFilter] = useState<string>('');
  const [workOrderDetailsModalData, setWorkOrderDetailsModalData] =
    useState<DetailModalState>({
      open: false,
      data: null,
    });
  const [deleteModalData, setDeleteModalData] = useState<DeleteModalState>({
    open: false,
    rowData: null,
    bulkData: null,
    dataType: null,
  });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const handleCloseDeleteModal = () => {
    setDeleteModalData({
      open: false,
      rowData: null,
      bulkData: null,
      dataType: null,
    });
  };
  const handleOpenDeleteModal = (
    rowData: ProcessedWorkOrder | null,
    bulkData: string[] | null,
    dataType: 'row' | 'bulk'
  ) => {
    if (dataType === 'row') {
      setDeleteModalData({ open: true, rowData, bulkData: null, dataType });
    } else if (dataType === 'bulk') {
      setDeleteModalData({ open: true, rowData: null, bulkData, dataType });
    }
  };

  const handleCloseWorkOrderDetailModal = () => {
    setWorkOrderDetailsModalData({ open: false, data: null });
  };

  const handleViewWorkOrder = (data: ProcessedWorkOrder) => {
    try {
      setWorkOrderDetailsModalData({
        open: true,
        data,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleEditWorkOrder = (data: ProcessedWorkOrder) => {
    try {
      if (data) {
        dispatch(updateBulk(data));
        const status = data?.status;
        if (
          status?.toLowerCase() === 'open' ||
          status?.toLowerCase() === 'in progress'
        ) {
          dispatch(setActiveStep(1));
        }
        handleCreateOrEditWorkOrderClick('tabular', 'update');
      } else {
        throw new Error('No work order data found!');
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleDeleteWorkOrder = async () => {
    try {
      const { rowData, dataType, bulkData } = deleteModalData;

      if (dataType === 'row') {
        const workOrderId = rowData?.workOrderId;
        if (workOrderId) {
          const componentIds: string[] = rowData.components
            .map((item) => item?.workOrderComponentId ?? '')
            .filter((id): id is string => id !== '');

          await workOrdersManagementHandler.deleteWorkOrder({
            workOrderId,
            components: componentIds,
          });
          if (workOrderDetailsModalData?.open) {
            handleCloseWorkOrderDetailModal();
          }
        } else {
          throw new Error('Work order id missing!');
        }
      } else if (dataType === 'bulk') {
        if (bulkData && bulkData?.length > 0) {
          await workOrdersManagementHandler.bulkDeleteWorkOrder(bulkData);
          setSelectedIds([]);
        } else {
          throw new Error('No selected rows!');
        }
      }
      // fetching again after deleting
      await workOrdersManagementHandler.getAllWorkOrders();
    } catch (error) {
      console.log(error);
    }
  };

  const handleExportData = async (ids: string[]) => {
    try {
      const offset = getCurrentUtcOffset(timezoneBasedOnDisplayMode);
      const payload: ExportWorkOrderPayload = {
        workOrderIds: ids,
        timeZoneOffset: offset,
        workflowType: 'work_order_creation',
      };
      const data = await workOrdersManagementHandler.exportWorkOrder(payload);
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, 'work_order_data');
    } catch (error) {
      console.log(error);
    }
  };

  const columnDefs: ColDef[] = [
    {
      checkboxSelection: true,
      headerCheckboxSelection: true,
      width: 50,
      pinned: 'left',
    },
    {
      field: 'createdAt',
      headerName: t('date_created'),
      minWidth: 140,
      flex: 1,
      resizable: true,
      valueGetter: (params: ValueGetterParams) => {
        const temp = convertUtcToDisplayTime(
          params?.data?.createdAt,
          preferredTimeDisplayMode,
          timezone,
          DATE_TIME_FORMAT
        );
        return temp;
      },
    },
    {
      field: 'workOrderNumber',
      headerName: t('work_order_no'),
      minWidth: 120,
      resizable: true,
      flex: 1,
    },
    {
      field: 'noOfIssues',
      headerName: t('number_of_issues'),
      minWidth: 120,
      resizable: true,
      flex: 1,
      valueGetter: (param) => {
        return Array.isArray(param?.data?.components)
          ? param.data.components.length
          : 0;
      },
    },
    {
      field: 'asset',
      headerName: t('asset'),
      minWidth: 240,
      resizable: true,
      flex: 1,
      valueGetter: (params) => {
        const {
          assetType = '',
          make = '',
          model = '',
          bumperNumber = '',
        } = params.data;
        return `${assetType} ${make} ${model} ${bumperNumber}`;
      },
      getQuickFilterText: (params) => {
        const {
          assetType = '',
          make = '',
          model = '',
          bumperNumber = '',
        } = params.data;
        return `${assetType} ${make} ${model} ${bumperNumber}`;
      },
      cellRendererFramework: (
        params: ICellRendererParams<ProcessedWorkOrder>
      ) => (
        <AssetDisplayCell
          assetType={params?.data?.assetType ?? ''}
          make={params?.data?.make ?? ''}
          model={params?.data?.model ?? ''}
          bumperNumber={params?.data?.bumperNumber ?? ''}
        />
      ),
    },
    // {
    //   field: 'componentId',
    //   headerName: 'Component',
    //   minWidth: 150,
    //   resizable: true,
    //   flex: 1,
    // },
    // {
    //   field: 'subComponentId',
    //   headerName: 'Sub Component',
    //   minWidth: 150,
    //   resizable: true,
    //   flex: 1,
    // },
    // {
    //   field: 'issueType',
    //   headerName: 'Issue Type',
    //   minWidth: 130,
    //   resizable: true,
    //   flex: 1,
    //   cellRendererFramework: (
    //     params: ICellRendererParams<ProcessedWorkOrder>
    //   ) => <Chip label={''} sx={statusStyle('')} />,
    // },
    // { field: 'source', headerName: 'Source', width: 120, resizable: true },
    {
      field: 'priority',
      headerName: t('priority'),
      minWidth: 120,
      flex: 1,
      cellRendererFramework: (
        params: ICellRendererParams<ProcessedWorkOrder>
      ) => (
        <Chip
          label={params.data?.priority}
          sx={statusStyle(params?.data?.priority ?? '')}
        />
      ),
      resizable: true,
    },
    {
      field: 'status',
      headerName: t('cdp_step_status'),
      minWidth: 130,
      flex: 1,
      cellRendererFramework: (
        params: ICellRendererParams<ProcessedWorkOrder>
      ) => (
        <Chip
          label={params.data?.status}
          sx={statusStyle(params.data?.status ?? '')}
        />
      ),
      resizable: true,
    },
    {
      field: 'actions',
      headerName: t('actions'),
      width: 145,
      pinned: 'right',
      sortable: false,
      filter: false,
      cellRendererFramework: (
        params: ICellRendererParams<ProcessedWorkOrder>
      ) => (
        <Box display={'flex'} alignItems={'center'} gap={1}>
          <Tooltip arrow title={t('view_details')}>
            <IconButton
              size="small"
              onClick={() => {
                if (params?.data) {
                  handleViewWorkOrder(params.data);
                }
              }}
              sx={{
                border: `1px solid ${palette.primary}`,
                borderRadius: '8px',
              }}
            >
              <RemoveRedEyeOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip arrow title={t('edit')}>
            <IconButton
              size="small"
              disabled={params?.data?.status?.toLowerCase() === 'completed'}
              onClick={() => {
                if (params?.data) {
                  handleEditWorkOrder(params.data);
                }
              }}
              sx={{
                border: `1px solid ${palette.primary}`,
                borderRadius: '8px',
              }}
            >
              <EditOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip arrow title={t('delete')}>
            <IconButton
              size="small"
              onClick={() => {
                if (params?.data) {
                  handleOpenDeleteModal(params.data, null, 'row');
                }
              }}
              sx={{
                border: '1px solid #EB5757',
                borderRadius: '8px',
              }}
            >
              <DeleteOutlineOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box
      borderRadius={'4px'}
      border={'1px solid'}
      borderColor={enableDarkTheme ? '#303A4F' : 'lightgrey'}
      bgcolor={enableDarkTheme ? palette.paperBackground : '#ffffff'}
      width={'100%'}
      height={'100%'}
    >
      <Box p={'8px'} display="flex" justifyContent="space-between">
        <Box display={'flex'} alignItems={'center'} gap={2}>
          <Typography fontSize={'14px'} fontWeight={600}>
            {t('work_order_created')}
          </Typography>
          <TextField
            variant="outlined"
            size="small"
            placeholder={t('search_asset_4')}
            value={quickFilter}
            onChange={(e) => {
              setQuickFilter(e.target.value);
            }}
            sx={{
              height: '30px',
            }}
            InputProps={{
              sx: {
                height: '30px',
              },
            }}
          />
        </Box>

        <Box
          display={'flex'}
          flexDirection={'row'}
          alignItems={'center'}
          gap={2}
        >
          <ImportButton />
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              void handleExportData([]);
            }}
            disabled={rowData?.length <= 0}
            sx={{
              textTransform: 'none',
            }}
            startIcon={<SimCardDownloadOutlined />}
          >
            {t('export')}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => {
              handleCreateOrEditWorkOrderClick('tabular', 'create');
            }}
            sx={{
              textTransform: 'none',
            }}
            startIcon={<AddCircleOutlineOutlined />}
          >
            {t('create_work_order_3')}
          </Button>
        </Box>
      </Box>
      <Box width={'100%'} height={'calc(100% - 48px)'} position={'relative'}>
        <TableWithLoader
          rowData={rowData}
          columnDefs={columnDefs}
          setSelectedIds={setSelectedIds}
          quickFilter={quickFilter}
        />
        <Box
          position={'absolute'}
          bottom={0}
          left={0}
          height={'48px'}
          display={'flex'}
          alignItems={'center'}
          px={'16px'}
          gap={2}
        >
          {selectedIds?.length > 0 && (
            <Typography>
              <span style={{ fontWeight: '500' }}>
                {`${selectedIds.length} ${
                  selectedIds.length === 1 ? 'row' : 'rows'
                } `}
              </span>
              selected
            </Typography>
          )}
          {selectedIds.length > 0 && (
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={() => {
                handleOpenDeleteModal(null, selectedIds, 'bulk');
              }}
              sx={{
                textTransform: 'none',
              }}
            >
              {t('delete_selected')}
            </Button>
          )}
          {selectedIds.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                void handleExportData(selectedIds);
              }}
              sx={{
                textTransform: 'none',
              }}
            >
              {t('export_selected')}
            </Button>
          )}
        </Box>
      </Box>
      {deleteModalData.open && (
        <ConfirmationModal
          open={deleteModalData.open}
          onConfirm={handleDeleteWorkOrder}
          onClose={handleCloseDeleteModal}
          title={t('confirm_deletion')}
          message={`Are you sure you want to delete? This action cannot be undone.`}
        />
      )}
      {workOrderDetailsModalData?.open && workOrderDetailsModalData?.data && (
        <WorkOrderDetailsModal
          open={workOrderDetailsModalData.open}
          onClose={handleCloseWorkOrderDetailModal}
          data={workOrderDetailsModalData?.data}
          handleEditClick={handleEditWorkOrder}
          handleDeleteClick={handleOpenDeleteModal}
        />
      )}
    </Box>
  );
};

export default Table;
